import {useSuspenseQuery} from "@apollo/client";
import {useCallback, useMemo, useRef} from "react";
import {useFormContext, useWatch} from "react-hook-form";

import {MultiServiceSelectCard} from "@/components/MultiServiceSelectCard/MultiServiceSelectCard.tsx";
import {GetServiceTypesAndTagsResult, GQL_GET_SERVICE_TYPES_AND_TAGS} from "@/lib/queries/statuspages.ts";
import {FieldProps} from "@/pages/checks/components/form/fields/common.ts";

type MultiServiceSelectCardFieldProps = Pick<FieldProps, "name" | "className" | "disabled"> & {
  label?: string;
};

export const MultiServiceSelectCardField = ({name, label, className}: MultiServiceSelectCardFieldProps) => {
  const {setValue, getValues} = useFormContext();
  const watchedValue = useWatch({name});
  const {data} = useSuspenseQuery<GetServiceTypesAndTagsResult>(GQL_GET_SERVICE_TYPES_AND_TAGS);
  const {monitoringServiceTypes, tags} = data;

  const formMethodsRef = useRef({setValue, getValues});
  formMethodsRef.current = {setValue, getValues};

  const defaultValue = useMemo(() => watchedValue.map((service: {id: number}) => service.id), [watchedValue]);

  const handleAppend = useCallback(
    (id: number) => {
      const {setValue, getValues} = formMethodsRef.current;
      setValue(name, [{id}, ...getValues(name)]);
    },
    [name]
  );

  const handleReplace = useCallback(
    (ids: number[]) => {
      const {setValue} = formMethodsRef.current;
      setValue(
        name,
        ids.map((id) => ({id}))
      );
    },
    [name]
  );

  return (
    <MultiServiceSelectCard
      title={label}
      className={className}
      defaultValue={defaultValue}
      onAppend={handleAppend}
      onReplace={handleReplace}
      monitoringServiceTypes={monitoringServiceTypes}
      tags={tags}
    />
  );
};
